/**
 * First-party visit tracking.
 *
 * Runs alongside PostHog, GA and Clarity rather than replacing them — it sends
 * their ids up with every beacon so the four can be reconciled later. That is
 * the whole reason it exists: the vendors disagree, and without a fourth
 * measurement that can be joined to the application's own data there is no way
 * to tell which of them is wrong.
 *
 * Session time is the part everyone gets wrong. `beforeunload` does not fire
 * reliably on mobile and never fires when a laptop lid closes, so a tracker
 * relying on it loses the end of most sessions and under-reports. This sends
 * on `visibilitychange` (which fires when a tab is hidden, including on the
 * way to being closed) and on a slow heartbeat, so the last durable figure is
 * never more than one heartbeat stale. Time only accrues while the tab is
 * actually visible — a tab left open in the background for six hours is not
 * six hours of attention.
 */
import { apiService } from '../services/apiService';
import { isViewAsOrPending } from '../services/viewAsState';

const VISITOR_KEY = 'utech_visitor_id';
const SESSION_KEY = 'utech_session_id';
const SESSION_AT = 'utech_session_at';
const SESSION_CAMPAIGN = 'utech_session_campaign';
// Matches the server's window, which matches what GA and Clarity use. The
// numbers are meant to be comparable to theirs.
const SESSION_GAP_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 30 * 1000;

type Ev = { kind: string; path?: string; action?: string; props?: Record<string, unknown> };

let _visitorId = '';
let _sessionId = '';
let _landing = '';
let _queue: Ev[] = [];
let _visibleMs = 0;
let _lastTick = 0;
let _timer: any = null;
let _started = false;

const rid = (p: string) =>
  `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

/** localStorage throws in some privacy modes; never let that break the page. */
const get = (k: string): string => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

function ids() {
  _visitorId = get(VISITOR_KEY) || rid('vis');
  set(VISITOR_KEY, _visitorId);
  const last = Number(get(SESSION_AT) || 0);
  const prev = get(SESSION_KEY);
  // A gap longer than the window starts a new visit, which is what makes
  // "sessions" mean the same thing here as in the vendors' dashboards. So
  // does arriving from a different campaign (GA's rule too): the server
  // keeps a visit's utm_* from its first beacon only, so someone already
  // browsing who then clicks a link in one of our e-mails would otherwise
  // never be counted as a visit from that e-mail.
  const campaign = new URLSearchParams(window.location.search).get('utm_campaign') || '';
  const sameVisit = !!prev && !!last && Date.now() - last < SESSION_GAP_MS
    && (!campaign || campaign === get(SESSION_CAMPAIGN));
  _sessionId = sameVisit ? prev : rid('sess');
  if (!sameVisit) set(SESSION_CAMPAIGN, campaign);
  set(SESSION_KEY, _sessionId);
  set(SESSION_AT, String(Date.now()));
}

function vendorIds() {
  const out: Record<string, string> = {};
  try {
    // GA writes _ga=GA1.2.<client id>; the client id is the last two fields.
    const ga = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
    if (ga) out.ga_client_id = ga[1];
    const clarity = (window as any).clarity?.v || get('_clck').split('|')[0];
    if (clarity) out.clarity_id = String(clarity).slice(0, 120);
    const ph = (window as any).posthog?.get_distinct_id?.();
    if (ph) out.posthog_id = String(ph).slice(0, 120);
  } catch { /* a missing vendor is not an error */ }
  return out;
}

function accrue() {
  const now = Date.now();
  if (_lastTick && document.visibilityState === 'visible') _visibleMs += now - _lastTick;
  _lastTick = now;
}

function payload() {
  accrue();
  const p = new URLSearchParams(window.location.search);
  return {
    session_id: _sessionId,
    visitor_id: _visitorId,
    user_id: apiService.getStoredUserId?.() || undefined,
    landing_path: _landing,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    utm_source: p.get('utm_source') || undefined,
    utm_medium: p.get('utm_medium') || undefined,
    utm_campaign: p.get('utm_campaign') || undefined,
    // An admin e-mail campaign's per-recipient link token (utm_content) is
    // what ties this visit to the person who clicked, before they sign in.
    utm_content: p.get('utm_content') || undefined,
    utm_term: p.get('utm_term') || undefined,
    duration_s: Math.round(_visibleMs / 1000),
    ...vendorIds(),
    events: _queue.splice(0, _queue.length),
  };
}

function send(final = false) {
  if (isViewAsOrPending()) return;
  try {
    const body = payload();
    if (!body.session_id) return;
    set(SESSION_AT, String(Date.now()));
    // sendBeacon survives the page going away, which a fetch does not. It is
    // the only reason the last beacon of a session ever arrives.
    if (final && navigator.sendBeacon) {
      navigator.sendBeacon(apiService.trackUrl(),
        new Blob([JSON.stringify(body)], { type: 'application/json' }));
      return;
    }
    void apiService.track(body);
  } catch { /* analytics must never surface to a visitor */ }
}

/** The ids this tab's beacons carry, for requests that must be joinable to the
 *  visit (the homepage search). In-memory first: another tab can start a new
 *  session and overwrite localStorage, but this tab's events keep its own. */
export function currentIds(): { visitor_id?: string; session_id?: string } {
  return {
    visitor_id: _visitorId || get(VISITOR_KEY) || undefined,
    session_id: _sessionId || get(SESSION_KEY) || undefined,
  };
}

export function trackPageview(path?: string) {
  _queue.push({ kind: 'pageview', path: path || window.location.pathname });
  send();
}

export function trackEvent(action: string, props?: Record<string, unknown>) {
  _queue.push({ kind: 'click', action, path: window.location.pathname, props });
  // Not sent immediately: clicks arrive in bursts and one request each would
  // be rude to the page. The heartbeat or the next pageview carries them.
  if (_queue.length >= 20) send();
}

export function startVisitorTracking() {
  // An admin viewing as a member (services/viewAsState.ts) is not a visit.
  if (_started || typeof window === 'undefined' || isViewAsOrPending()) return;
  _started = true;
  ids();
  _landing = window.location.pathname;
  _lastTick = Date.now();
  trackPageview();
  _timer = setInterval(() => { accrue(); send(); }, HEARTBEAT_MS);
  document.addEventListener('visibilitychange', () => {
    accrue();
    // Hidden is the closest thing to "leaving" that fires reliably on mobile.
    send(document.visibilityState === 'hidden');
  });
  window.addEventListener('pagehide', () => { accrue(); send(true); });
}

export function stopVisitorTracking() {
  if (_timer) clearInterval(_timer);
  _timer = null;
  _started = false;
}
